import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { AgeRangesDataService, AgeRangesColumnsService } from '../../services';
import { DialogAgeRangeFormComponent } from './age-range-form/age-range-form.component';
import { DialogAgeRangeDetailsComponent } from './age-range-details/age-range-details.component';
import { AgeRange, AgeRangesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-age-ranges',
  templateUrl: './age-ranges.component.html',
  styleUrls: ['./age-ranges.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class AgeRangesComponent implements OnInit, OnDestroy {

  private ageRangeAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private ageRangeEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private ageRangeDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public ageRangeToDelete: AgeRange = {};
  public ageRanges: AgeRange[] = [];
  public ageRange: AgeRange = {};
  public selectedAgeRanges: AgeRange[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private ageRangesDataService: AgeRangesDataService,
    private ageRangesColumnsService: AgeRangesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('AgeRangesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Age Ranges - Management');
    this.primengConfig.ripple = true;
    this.getAgeRanges();
  }

  ngOnDestroy(): void {
    console.log('AgeRangesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getAgeRanges(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.ageRangesDataService.getAgeRanges(this.parameters).subscribe((response: AgeRangesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.ageRanges = response.results;
        } else {
          this.ageRanges = this.ageRanges.concat(response.results);
        }
        if (response.inlineCount > this.ageRanges.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getAgeRanges();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('AGERANGESNOTLOADED')});
    });
  }

  importAgeRanges(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.ageRangesDataService.addAgeRanges(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('AGERANGESCREATED')});
                if (proceed === j) {
                  this.getAgeRanges();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('AGERANGESNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('AGERANGESNOTCREATED') + ' ' + msg});
            });
          } else {
            this.ageRangesDataService.updateAgeRanges(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('AGERANGESUPDATED')});
                if (proceed === j) {
                  this.getAgeRanges();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('AGERANGESNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('AGERANGESNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.ageRangesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('ageRanges.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedAgeRanges() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Age Ranges?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.ageRangesDataService.deleteAgeRanges(this.selectedAgeRanges).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Age Ranges Deleted', life: 3000});
            this.getAgeRanges();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Age Ranges not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Age Ranges not deleted'});
        });
        this.selectedAgeRanges = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.ageRanges, this.columns, 'AgeRanges');
  }

  exportExcel() {
    ToolsService.exportExcel(this.ageRanges, this.columns, 'AgeRanges');
  }

  exportCsv() {
    ToolsService.exportCsv(this.ageRanges, this.columns, 'AgeRanges');
  }

  refresh() {
    this.ageRangesDataService.clearCache();
    this.getAgeRanges();
  }

  ageRangeAdd(): void {
    if (!this.navigation.getParamValue('ageRanges.addInDialog', true)) {
      this.router.navigate(['/age-ranges/add']);
      return;
    }
    this.ageRangeAddDialog = this.dialogService.open(DialogAgeRangeFormComponent, {
      header: '', // this.translate.instant('NEWAGERANGE'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        ageRange: {id: null},
        users: this.ageRangesColumnsService.users,
      },
    });

    this.ageRangeAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'AgeRange added'});
        this.getAgeRanges();
      }
    });
  }

  ageRangeEdit(ageRange: AgeRange): void {
    if (!this.navigation.getParamValue('ageRanges.editInDialog', true)) {
      this.router.navigate(['/age-ranges/edit/' + ageRange.id]);
      return;
    }
    this.ageRangeEditDialog = this.dialogService.open(DialogAgeRangeFormComponent, {
      header: '', // this.translate.instant('EDITAGERANGE') + ' ' + ageRange.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        ageRange: ageRange,
        users: this.ageRangesColumnsService.users,
      },
    });

    this.ageRangeEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'AgeRange updated'});
        this.getAgeRanges();
      }
    });
  }

  ageRangeDetails(ageRange: AgeRange): void {
    if (!this.navigation.getParamValue('ageRanges.detailsInDialog', true)) {
      this.router.navigate(['/age-ranges/details/' + ageRange.id]);
      return;
    }
    this.ageRangeDetailsDialog = this.dialogService.open(DialogAgeRangeDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        ageRange: ageRange,
      }
    });
  }

  ageRangeDelete(ageRange: AgeRange): void {
    this.ageRangeToDelete = ageRange;
    this.messageService.clear();
    this.messageService.add({
      key: 'ageRangeDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + ageRange.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.ageRangesDataService.deleteAgeRange(this.ageRangeToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Age Range deleted'});
      this.getAgeRanges();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Age Range not deleted'});
    });
  }

  onDeleteReject(): void {
    this.ageRangeToDelete = {};
    this.messageService.clear('ageRangeDelete');
  }

}
